import { useState } from "react";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import { MaterialModalForm, type MaterialFormPayload } from "./MaterialModalForm";
import type { MfgModalKind } from "./mfgPanelTypes";
import type { CreateMaterialPayload } from "./useManufacturingCreateMaterial";
import type { UpdateMaterialPayload } from "./useManufacturingUpdateMaterial";
import { mfgModalShellClass, mfgSecondaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

type SaveResult = { ok: boolean; error?: string };

type Props = {
  openModal: MfgModalKind;
  setOpenModal: (v: MfgModalKind | null) => void;
  /** When set, the modal opens in edit mode against this material. */
  editingMaterial?: MfgMaterial;
  /**
   * Categories already present on this org's materials. Forwarded to
   * `MaterialModalForm` so the category dropdown surfaces real labels
   * before the operator has to type one in. Optional + defaults to empty.
   */
  existingCategories?: readonly string[];
  existingUnits?: readonly string[];
  existingShippingUnits?: readonly string[];
  canEditCode?: boolean;
  onCreateMaterial: (payload: CreateMaterialPayload) => Promise<SaveResult>;
  onUpdateMaterial?: (payload: UpdateMaterialPayload) => Promise<SaveResult>;
};

export function ManufacturingPanelModal({
  openModal,
  setOpenModal,
  editingMaterial,
  existingCategories,
  existingUnits,
  existingShippingUnits,
  canEditCode,
  onCreateMaterial,
  onUpdateMaterial,
}: Props) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isEdit = !!editingMaterial;

  async function handleSaveMaterial(payload: MaterialFormPayload) {
    setSubmitError(null);
    let result: SaveResult;
    if (isEdit && editingMaterial && onUpdateMaterial) {
      result = await onUpdateMaterial({
        material_id: editingMaterial.id,
        name: payload.name,
        ...(payload.code !== undefined ? { code: payload.code } : {}),
        unit: payload.unit,
        category: payload.category,
        shipping_unit: payload.shipping_unit,
        shipping_unit_weight: payload.shipping_unit_weight,
        pieces_per_shipping_unit: payload.pieces_per_shipping_unit,
        vendor: payload.vendor,
        primary_alias_id: payload.primary_alias_id,
        msds_status: payload.msds_status,
        reorder_threshold: payload.reorder_threshold,
        allow_zero_cost: payload.allow_zero_cost,
      });
    } else {
      result = await onCreateMaterial({
        code: payload.code,
        name: payload.name,
        unit: payload.unit,
        category: payload.category,
        shipping_unit: payload.shipping_unit,
        shipping_unit_weight: payload.shipping_unit_weight,
        pieces_per_shipping_unit: payload.pieces_per_shipping_unit,
        vendor: payload.vendor || undefined,
        primary_alias_id: payload.primary_alias_id || undefined,
        msds_status: payload.msds_status,
        reorder_threshold: payload.reorder_threshold,
        allow_zero_cost: payload.allow_zero_cost,
      });
    }
    if (result.ok) {
      setOpenModal(null);
      return;
    }
    setSubmitError(result.error || (isEdit ? "Failed to update material" : "Failed to create material"));
  }

  const title =
    openModal === "building"
      ? "New Building"
      : openModal === "material"
        ? isEdit
          ? "Edit Material"
          : "New Material"
        : openModal === "vendor"
          ? "New Vendor"
          : "Add User";

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={() => setOpenModal(null)}
    >
      <div
        // max-h-[90vh] + overflow-y-auto so the New/Edit Material form can
        // scroll internally on short viewports (Mac 1445x883 with browser
        // chrome leaves ~700px; the 7-field form overflows). Matches
        // ManufacturingBulkImportModal's pattern. Dev-sync note
        // 86bcdf21 (+ 2 duplicates 7a430d54 / 0986844c).
        className={`max-h-[90vh] w-full max-w-md overflow-y-auto p-5 sm:rounded-2xl rounded-t-2xl ${mfgModalShellClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">{title}</h3>
        <div className="mt-3">
          {openModal === "material" ? (
            <>
              {submitError ? (
                <div className="mb-3 text-xs text-red-600 dark:text-red-400">{submitError}</div>
              ) : null}
              <MaterialModalForm
                editingMaterial={editingMaterial}
                existingCategories={existingCategories}
                existingUnits={existingUnits}
                existingShippingUnits={existingShippingUnits}
                canEditCode={canEditCode}
                onSave={handleSaveMaterial}
                onCancel={() => setOpenModal(null)}
              />
            </>
          ) : (
            <>
              <p className="text-xs text-gray-600 dark:text-zinc-300">
                This modal is scaffolded and will be wired in a future slice.
              </p>
              <div className="mt-4 flex justify-end">
                <button type="button" className={mfgSecondaryBtnClass} onClick={() => setOpenModal(null)}>
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
